import React, { useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import Shop from './pages/Shop';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import OrderConfirmation from './pages/OrderConfirmation';
import TrackOrder from './pages/TrackOrder';
import About from './pages/About';
import Contact from './pages/Contact';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { getApiUrl, getBaseUrl } from './utils/apiUrl';
import { logServerStatus } from './utils/serverCheck';
import './App.css';

// Configure axios base URL - auto-detect based on hostname
axios.defaults.baseURL = getApiUrl();

function App() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
    // Check server status on app load (only in development or when explicitly enabled)
    if (process.env.NODE_ENV === 'development' || window.location.search.includes('checkServer=true')) {
      logServerStatus();
    }
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/settings');
      // Ensure settings is always an object, even if API returns unexpected format
      setSettings(response.data?.settings || response.data || {});
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      // Ensure settings is always an object even on error
      setSettings({});
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  // Get favicon URL - use logo if available, otherwise use default
  const faviconUrl = settings?.site_logo 
    ? `${getBaseUrl()}/favicon.ico`
    : '/favicon.ico';

  return (
    <AuthProvider>
      <CartProvider>
        <div className="App">
          <Helmet>
            <title>{settings?.site_name || 'NieHairLabel'} - Premium Wigs</title>
            <meta name="description" content={settings?.site_description || 'Premium Quality Wigs'} />
            <meta name="keywords" content="wigs, hair, premium, quality, NieHairLabel" />
            <meta property="og:title" content={settings?.site_name || 'NieHairLabel'} />
            <meta property="og:description" content={settings?.site_description || 'Premium Quality Wigs'} />
            <meta property="og:type" content="website" />
            <link rel="canonical" href={window.location.href} />
            <link rel="icon" href={faviconUrl} type="image/x-icon" />
          </Helmet>
          <Header settings={settings} />
          <main>
            <Routes>
              <Route path="/" element={<Home settings={settings} />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/shop/:slug" element={<ProductDetail />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/order-confirmation/:orderNumber" element={<OrderConfirmation />} />
              <Route path="/track-order" element={<TrackOrder />} />
              <Route path="/about" element={<About settings={settings} />} />
              <Route path="/contact" element={<Contact settings={settings} />} />
              <Route path="/hair-wp/login" element={<AdminLogin />} />
              <Route path="/hair-wp/*" element={<AdminDashboard />} />
            </Routes>
          </main>
          <Footer settings={settings} />
        </div>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;

